import {
  leadsSequencesDestroyMutation,
  leadsSequencesPartialUpdateMutation,
  PatchedLeadSequenceUpdate,
  ReturnShortLeadReadable,
  ReturnShortLeadSequenceReadable,
  sequencesLeadsListOptions,
  sequencesLeadsListQueryKey,
  sequencesStepsListListOptions
} from '@crm/api'
import { AppLink, ConfirmModal } from '@crm/components'
import { CampaignLeadsFilters } from '@crm/features/campaigns/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useStateHandlers } from '@crm/hooks/useStateHandlers'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { CampaignStatus, Properties } from '@crm/types'
import { defaultFilters, defaultNonPagination, TagStatuses } from '@crm/utils'
import {
  ConditionalText,
  TableActions,
  TableContainer
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Flex, Form, Modal, notification, Radio, Select, Tag } from 'antd'
import { Edit2, Status, Trash } from 'iconsax-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import type { MenuProps, TableColumnsType } from 'antd'

const formStepName = 'edit-current-step-id'
const formIdStatusName = 'edit-status-id'

const defaultState: {
  edit: boolean
  delete: boolean
  leadId?: string
  status: boolean
} = {
  edit: false,
  delete: false,
  status: false,
  leadId: undefined
}

const statusOnChange = {
  [CampaignStatus.SUCCESS]: 'success',
  [CampaignStatus.FAILED]: 'danger',
  [CampaignStatus.ACTIVE]: 'warning'
}

export const CampaignLeads = () => {
  const { t } = useLocales()
  const { id: campaignId } = useParams()
  const params = useQueryParams()
  const navigate = useAppNavigate()
  const [form] = Form.useForm()
  const { resetPagination } = useResetPagination()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const sorter = useTableSorter()

  const [state, setState] = useStateHandlers(defaultState)

  const { data, isLoading } = useQuery({
    ...sequencesLeadsListOptions({
      path: { sequence_id: String(campaignId) },
      query: {
        ...defaultFilters,
        ...params
      }
    })
  })

  const { data: steps } = useQuery({
    ...sequencesStepsListListOptions({
      path: { sequence_id: String(campaignId) },
      query: {
        ...defaultNonPagination
      }
    })
  })

  useEffect(() => {
    if (data && state.leadId) {
      const lead = data.results.find(
        (i: ReturnShortLeadSequenceReadable) =>
          i?.lead?.id === Number(state.leadId)
      )

      if (state.edit) {
        form.setFieldsValue({ current_step: lead?.current_step?.id })
      }
      if (state.status) {
        form.setFieldsValue({ status: lead?.status })
      }
    }
  }, [data, state.leadId, state.edit, state.status])

  const onSuccessMutation = (descriptionMessage?: string) => {
    invalidateQueries([
      sequencesLeadsListQueryKey({
        path: { sequence_id: String(campaignId) }
      })
    ])

    setState(defaultState)

    notification.success({
      message: t('success.success'),
      description: t(`success.${descriptionMessage}`)
    })
  }

  const { mutate: patchLeadsSequencesMutate, isPending: isPatchPending } =
    useMutation({
      ...leadsSequencesPartialUpdateMutation(),
      onError: (e) => showApiErrors(e),
      onSuccess: () => onSuccessMutation('success_data_save')
    })

  const { mutate: deleteLeadsSequencesMutate, isPending: isDeletePending } =
    useMutation({
      ...leadsSequencesDestroyMutation(),
      onError: (e) => showApiErrors(e),
      onSuccess: () => {
        onSuccessMutation('success_data_delete')
        resetPagination()
      }
    })

  const onDelete = () => {
    if (state.leadId && state.delete) {
      deleteLeadsSequencesMutate({
        path: {
          lead_id: state.leadId,
          sequence: Number(campaignId)
        }
      })
    }
  }

  const onCloseModal = () => setState(defaultState)

  const onRow = ({ lead }: ReturnShortLeadSequenceReadable): Properties => ({
    onClick: () => navigate(`/leads/${lead?.id}`)
  })

  const statusOptions = useMemo(
    () =>
      Object.keys(statusOnChange).map((status) => ({
        label: (
          <Tag
            color={
              TagStatuses[
                statusOnChange[
                  status as keyof typeof statusOnChange
                ] as keyof typeof TagStatuses
              ]
            }
          >
            {t(`campaigns.stats_${status}`)}
          </Tag>
        ),
        value: status
      })),
    [t]
  )

  const getActionColumnItem: (
    lead: ReturnShortLeadReadable
  ) => MenuProps['items'] = (lead) => [
    {
      label: t('contacts.change_status'),
      key: 'changeStatus',
      icon: <Status size={20} />,
      onClick: () =>
        setState({
          leadId: String(lead.id),
          status: true,
          delete: false,
          edit: false
        })
    },
    {
      label: t('buttons.edit'),
      icon: <Edit2 size={20} />,
      key: 'edit',
      onClick: () =>
        setState({ leadId: String(lead.id), edit: true, delete: false })
    },
    { type: 'divider' },
    {
      label: t('buttons.delete'),
      key: 'delete',
      icon: <Trash size={20} />,
      danger: true,
      onClick: () =>
        setState({ leadId: String(lead.id), delete: true, edit: false })
    }
  ]

  const columns: TableColumnsType<ReturnShortLeadSequenceReadable> = useMemo(
    () => [
      {
        title: t('campaigns.name'),
        key: 'name',
        sorter: true,
        render: ({ lead }) => (
          <AppLink to={`/lead-list/${lead.id}`}>
            {lead.name}
            {lead.idy}
          </AppLink>
        )
      },
      {
        title: t('campaigns.status'),
        dataIndex: 'status'
      },
      {
        title: t('nomenclature.tags'),
        render: (_, { lead }) =>
          lead.tags.length ? (
            <Flex gap="8px 0" wrap>
              {lead?.tags.map(({ color, name, id }) => (
                <Tag key={id} color={color}>
                  {name}
                </Tag>
              ))}
            </Flex>
          ) : (
            '-'
          )
      },
      {
        title: t('campaigns.step'),
        render: ({ current_step }) => (
          <ConditionalText condition={current_step?.title}>
            {current_step?.title}
          </ConditionalText>
        )
      },
      {
        width: 50,
        key: 'actions',
        fixed: 'right',
        render: ({ lead }) => (
          <TableActions options={getActionColumnItem(lead)} />
        )
      }
    ],
    [t, data, params]
  )

  const onSubmit = ({ current_step, status }: PatchedLeadSequenceUpdate) =>
    patchLeadsSequencesMutate({
      path: {
        lead_id: String(state?.leadId),
        sequence: Number(campaignId)
      },
      body: {
        ...(current_step && { current_step }),
        ...(status && { status })
      }
    })

  return (
    <Flex gap={16} vertical>
      <TableContainer
        loading={isLoading}
        data={{ results: data?.results, count: data?.count }}
        columns={columns}
        showSorterTooltip={false}
        scroll={{ y: 'calc(100vh - 400px)' }}
        onChange={(_, __, result) => sorter(result)}
        onRow={onRow}
        leftChildren={<CampaignLeadsFilters />}
      />

      <ConfirmModal
        open={!!state.leadId && state.delete}
        onSuccess={onDelete}
        onCancel={onCloseModal}
      />

      <Modal
        open={!!state.leadId && state.edit}
        title={t('campaigns.edit_step')}
        onCancel={onCloseModal}
        destroyOnHidden
        cancelText={t('buttons.cancel')}
        okText={t('buttons.edit')}
        okButtonProps={{
          htmlType: 'submit',
          form: formStepName,
          loading: isPatchPending || isDeletePending
        }}
      >
        <Form name={formStepName} form={form} onFinish={onSubmit}>
          <Form.Item name="current_step">
            <Select
              placeholder="Select"
              options={steps?.results.map((item) => ({
                value: item.key,
                label: item.label
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!state.leadId && state.status}
        title={t('contacts.change_status')}
        onCancel={onCloseModal}
        destroyOnHidden
        cancelText={t('buttons.cancel')}
        okText={t('buttons.edit')}
        okButtonProps={{
          htmlType: 'submit',
          form: formIdStatusName,
          loading: isPatchPending || isDeletePending
        }}
      >
        <Form name={formIdStatusName} form={form} onFinish={onSubmit}>
          <Form.Item name="status">
            <Radio.Group options={statusOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  )
}
